import {
    Checkbox,
    FormLabel,
    IdOptionType,
    OptionType,
    Radiogroup,
    RadiogroupOptions,
    SearchableSelect,
    SelectList,
    Textinput
} from "shared";
import {
    ITemplateComponent,
    ITemplateProcureComponent
} from "entities/template";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { LabelField } from "../../form_fields/label_field/label_field";
import { VisibilityField } from "../../form_fields/visibility_field/visibility_field";
import { TooltipField } from "../../form_fields/tooltip_field/tooltip_field";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { useComponentValidateFunction } from "../../../../lib/hooks/useComponentValidateFunction";
import { getDefaultIprocStyles, IPROC_SUBTYPE_PROP, IPROC_SUBTYPES } from "../../../../lib/utils/iproc_integration";
import { constructOptions } from "../../../../lib/utils/dynamic_document_form_utils";
import styles from "./component_form_procure.module.scss";

interface IComponentFormProcureProps {
    component: ITemplateProcureComponent;
    enLanguageEnabled: boolean;
    componentList: ITemplateComponent[];
    onChange: (component: ITemplateComponent) => void;
    triggerValidate: number;
}

export const ComponentFormProcure: React.FC<IComponentFormProcureProps> = React.memo(({
                                                                                          component,
                                                                                          triggerValidate,
                                                                                          enLanguageEnabled,
                                                                                          componentList,
                                                                                          onChange
                                                                                      }) => {
    const optionMap = (value: string, id: number) => {
        return {
            value,
            label: value,
            id
        };
    };

    const t = useComponentsTranslations();
    const [listIdModifier, setListIdModifier] = useState<number>(0);
    const [stylesButtonGroup, setStylesButtonGroup] = useState<ReactNode>();
    const [stylesOptions] = useState<IdOptionType[]>(getDefaultIprocStyles().map(optionMap));
    const [defaultValueOptions, setDefaultValueOptions] = useState<IdOptionType[]>([]);
    const { requiredFieldValidation } = useComponentValidateFunction();

    const additionalDefaultOptions = useMemo(() => {
        return constructOptions(IPROC_SUBTYPES);
    }, []);

    useEffect(() => {
        if (!component.list || component.list.length === 0) {
            onFieldChange({ list: getDefaultIprocStyles() });
            setListIdModifier(1000);
        }
    }, []);


    const onRenderButtonGroup = (node: ReactNode) => {
        setStylesButtonGroup(node);
    };

    useEffect(() => {
        Array.isArray(component.list) && setDefaultValueOptions(component.list.map(optionMap));
    }, [component.list]);

    const onFieldChange = (value: Partial<ITemplateProcureComponent>) => {
        onChange({
            ...component,
            ...value
        });
    };

    const onInputListChange = (list: string[]) => {
        let defaultValue: string | undefined = undefined;

        if (component.defaultValue && list.includes(component.defaultValue)) {
            defaultValue = component.defaultValue;
        }

        onChange({
            ...component,
            list,
            defaultValue
        });
    };

    const documentFormatOptions: RadiogroupOptions[] = useMemo(() => {
        const options = [{
            label: "PDF",
            value: "undefined"
        }, {
            label: "Word",
            value: "rtf"
        }];

        return options;
    }, []);

    return <div className={styles.container}>
        <div className={styles.description}>{t("procureComponentDescription")}</div>
        <LabelField component={component}
                    enLanguageEnabled={enLanguageEnabled}
                    title={t("formLabel")}
                    name="label-dynamic-input"
                    onChange={onFieldChange}/>
        <FormLabel title={t("trackerQueue")} required={true}>
            <Textinput size="m"
                       value={component.startrekQueueIproc ? component.startrekQueueIproc : ""}
                       triggerValidate={triggerValidate}
                       validate={true}
                       validateFunc={requiredFieldValidation}
                       onChange={(e) => onFieldChange({ startrekQueueIproc: e.target.value })}
                       view="outline"/>
            <div className={styles.subLabel}>{"* " + t("trackerQueueDescription")}</div>
        </FormLabel>
        <div className={styles.listActionContainer}>
            <div className={styles.listActionButtons}>
                {stylesButtonGroup}
            </div>
            <FormLabel title={t("purchaseRequestStyleList")}>
                <SelectList
                    value={component.list}
                    className={styles.selectList}
                    options={stylesOptions}
                    resetFactor={component.id as number + listIdModifier}
                    renderButtonGroup={onRenderButtonGroup}
                    onChange={onInputListChange}/>
            </FormLabel>
        </div>

        <FormLabel title={t("defaultPurchaseRequestStyle")}>
            <SearchableSelect options={defaultValueOptions}
                              optionValue={defaultValueOptions.find((o) => o.value === component.defaultValue) as OptionType}
                              onChange={(value) => onFieldChange({
                                  defaultValue: value as string,
                                  additionalDefaultValue: undefined
                              })}
                              triggerValidate={triggerValidate}
                              validate={component.hideIprocStyle}
                              isMulti={false}
                              hideClearIcon={false}
                              isSearchable={true}/>
        </FormLabel>
        {component.defaultValue === IPROC_SUBTYPE_PROP && <FormLabel title={t("defaultPurchaseRequestSubtype")}>
            <SearchableSelect options={additionalDefaultOptions}
                              optionValue={additionalDefaultOptions.find((o) => o.value === component.additionalDefaultValue) as OptionType}
                              onChange={(value) => onFieldChange({ additionalDefaultValue: value as string })}
                              isMulti={false}
                              hideClearIcon={false}
                              isSearchable={true}/>
        </FormLabel>}
        <Checkbox
            label={t("hideIprocStyle")}
            size="m"
            onChange={() => onFieldChange({ hideIprocStyle: !component.hideIprocStyle })}
            checked={component.hideIprocStyle}
        />
        <Checkbox
            label={t("templateContract")}
            size="m"
            onChange={() => onFieldChange({
                isTemplate: !component.isTemplate,
                ticketNum: undefined
            })}
            checked={component.isTemplate}
        />
        {component.isTemplate && <FormLabel title={t("ticketWithApprovals")} required={true}>
            <Textinput size="m"
                       triggerValidate={triggerValidate}
                       validate={true}
                       validateFunc={requiredFieldValidation}
                       value={component.ticketNum ? component.ticketNum : ""}
                       onChange={(e) => onFieldChange({ ticketNum: e.target.value })}
                       view="outline"/>
        </FormLabel>}
        <VisibilityField
            component={component}
            componentList={componentList}
            onChange={onFieldChange}/>
        <TooltipField
            component={component}
            enLanguageEnabled={enLanguageEnabled}
            onChange={onFieldChange}
        />
        <FormLabel title={t("documentFormat")}>
            <Radiogroup vertical={false}
                        name="ipoc-document-form-radio"
                        options={documentFormatOptions}
                        onChange={(val) => {
                            onFieldChange({ format: val === "undefined" ? undefined : val as "rtf" });
                        }}
                        value={component.format ? component.format : "undefined"}
            />
        </FormLabel>
    </div>;
});
